import { format } from 'date-fns';

// Search and date filtering for the document lists on the Bullhorn HyreDocs
// tabs (Candidate, Contact, Placement and Company). Pure functions, shared by
// every tab so they all behave identically.

const SENT_DATE_FORMATS = ['d MMM yyyy', 'd MMMM yyyy', 'MMM d, yyyy', 'MMMM yyyy', 'dd/MM/yyyy', 'yyyy-MM-dd'];

const sentIso = (doc) => doc?.sent_at || doc?.created_at || null;

function localDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const norm = (value) => String(value ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

// Everything a recruiter might type to find a document, lower-cased, built once
// per document. The sent date is written out the ways people say or type it
// ("22 Sep 2026", "Sep 22, 2026", "22/09/2026", "2026-09-22", "september 2026").
const cache = new WeakMap();
function haystack(doc) {
  const hit = cache.get(doc);
  if (hit) return hit;
  const sent = localDate(sentIso(doc));
  const placement = doc?.placement_id ? [`placement ${doc.placement_id}`, `#${doc.placement_id}`, String(doc.placement_id)] : [];
  const text = norm([
    doc?.template_name,
    doc?.packet_name,
    doc?.candidate_name,
    doc?.candidate_email,
    doc?.company_name,
    doc?.status ? String(doc.status).replace(/_/g, ' ') : '',
    ...placement,
    ...(sent ? SENT_DATE_FORMATS.map((pattern) => format(sent, pattern)) : []),
  ].filter(Boolean).join(' | '));
  cache.set(doc, text);
  return text;
}

/** True when every word of the query appears somewhere in the document's searchable text. */
export function matchesQuery(doc, query) {
  const terms = norm(query).split(' ').filter(Boolean);
  if (terms.length === 0) return true;
  const text = haystack(doc);
  return terms.every((term) => text.includes(term));
}

/** from / to are 'YYYY-MM-DD' strings (either may be empty); both ends are inclusive. */
export function inDateRange(doc, from, to) {
  if (!from && !to) return true;
  const sent = localDate(sentIso(doc));
  if (!sent) return false;
  let lo = from || '';
  let hi = to || '';
  if (lo && hi && lo > hi) [lo, hi] = [hi, lo]; // a reversed range means the same days
  const day = format(sent, 'yyyy-MM-dd');
  return (!lo || day >= lo) && (!hi || day <= hi);
}

export const NO_FILTERS = Object.freeze({ query: '', from: '', to: '' });

export const hasActiveFilters = (filters) => !!(filters && (norm(filters.query) || filters.from || filters.to));

export function applyDocumentFilters(documents, filters) {
  const list = documents || [];
  if (!hasActiveFilters(filters)) return list;
  return list.filter((doc) => matchesQuery(doc, filters.query) && inDateRange(doc, filters.from, filters.to));
}
